import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { Button, Input, Label } from "@/components/ui/form";
import { useAuth } from "@/state/AuthContext";
import { apiFetch } from "@/utils/api";

export function LoginPage() {
  const { token, setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (token) return <Navigate to="/" replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await apiFetch<{ token: string; user: Parameters<typeof setSession>[1] }>(
        "/auth/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
          token: null,
        },
      );
      setSession(data.token, data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-svh flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-argon-header via-[#1e2746] to-[#111827] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,#5e72e4_0%,transparent_50%)]" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-argon-primary text-lg font-bold shadow-lg">
              M
            </div>
            <div>
              <p className="text-lg font-bold">MWL Lumber</p>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl font-bold leading-tight mb-4">Manage lumber quotes with clarity.</h1>
          <p className="text-white/70 text-lg leading-relaxed">
            Track RFQs from draft through seller quotes, customer approval, and purchase orders — all in one place.
          </p>
        </div>
        <p className="relative text-sm text-white/50">© {new Date().getFullYear()} MWL Lumber</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-argon-body">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-argon-primary text-white font-bold">
              M
            </div>
            <div>
              <p className="font-bold text-argon-dark">MWL Lumber</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-[0_0_2rem_0_rgba(136,152,170,0.15)] p-8">
            <h2 className="text-xl font-bold text-argon-dark mb-1">Sign in</h2>
            <p className="text-sm text-argon-secondary mb-6">Enter your email and password to sign in</p>
            <form className="space-y-5" onSubmit={onSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-argon-danger">
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
